<script lang="tsx">
  import { computed, ref, useId, type PropType } from "vue";
  import { Button, Checkbox, Dialog, FloatLabel, InputText, Message, Password, Select, Textarea } from "primevue";
  import PasswordStrengthSuspend from "./PasswordStrengthSuspend.vue";
  import { Form, FormField as PrimeFormField, type FormInstance, type FormResolverOptions, type FormSubmitEvent } from "@primevue/forms";

  import { FontAwesomeIcon as Icon } from "@fortawesome/vue-fontawesome";
  import { faArrowRight, faCircleNotch } from "@fortawesome/free-solid-svg-icons";

  import { useQuery } from "@urql/vue";
  import { graphql as gql } from "@/gql";

  import municipalRegions from "@/data/municipal_regions.json";
  import usStates from "@/data/states.json";
  import provinces from "@/data/provinces.json";
  import countries from "@/data/countries.json";
  import memberTypes from "@/data/member_type.json";
  import memberStatuses from "@/data/member_statuses.json";
  import languages from "@/data/languages.json";

  import { formatPhoneNumber, formatPostalCode, isEmailValid, isPostalCodeValid, isUrlValid, resolvePhoneNumber } from "@/lib/forms";
  import type { UserOptions } from "@/lib/craft";
  import type { User } from "@/gql/graphql";


  export default {
    components: {
      Form, Dialog,
      Button, Icon, PrimeFormField, Message, Checkbox, FloatLabel,
      Password, InputText, Select, Textarea,
      PasswordStrengthSuspend,
    },
    props: {
      site: {
        type: String as PropType<"enca" | "frca">,
        required: true,
      }
    },
    async setup() {
      const vid = useId();
      const result = await useQuery({
        query: gql(`
          query QueryCurrentUserInfo {
            currentUser {
              ... on User {
                id
                username
                fullName
                email
                preferredLanguage

                organization
                memberNumber
                memberType
                memberStatus
                numSites

                phone {
                  number
                  international: format(format: "international")
                  tel: format(format: "rfc3966")
                }
                fax {
                  number
                  international: format(format: "international")
                  tel: format(format: "rfc3966")
                }
                website {
                  url
                }

                billingAddress {
                  id
                  addressLines
                  country
                  state
                  province
                  region
                  city
                  postalCode
                }
              }
            }
          }
        `)
      });


      return {
        currentUser: computed(() => result.data.value!.currentUser),
        formComponent: ref(undefined as FormInstance | undefined),
        vid,
      };
    },
    data() {
      // Vue simply cannot do type inference in data() for the public instance proxy
      // For us, setup variables fail type inference (but still work)
      // https://github.com/vuejs/vue/issues/12427
      const currentUser = this.currentUser as any as User;

      return {
      initialValues: {
        fullName: currentUser.fullName ?? undefined,
        username: currentUser.username ?? undefined,
        email: currentUser.email ?? undefined,
        preferredLanguage: currentUser.preferredLanguage,

        changePassword: false,
        password: "",

        organization: currentUser?.organization ?? undefined,
        memberNumber: currentUser?.memberNumber != null ? currentUser.memberNumber.toString() : undefined,
        memberType: currentUser?.memberType ?? undefined,
        memberStatus: currentUser?.memberStatus ?? undefined,
        numSites: currentUser?.numSites != null ? currentUser.numSites.toString() : undefined,

        phone: currentUser?.phone?.number ?? undefined,
        fax: currentUser?.fax?.number ?? undefined,
        website: currentUser?.website?.url ?? undefined,

        addressLines: currentUser?.billingAddress?.addressLines ?? undefined,
        country: currentUser?.billingAddress?.country ?? undefined,
        province: currentUser?.billingAddress?.province ?? undefined,
        state: currentUser?.billingAddress?.state ?? undefined,
        region: currentUser?.billingAddress?.region ?? undefined,
        city: currentUser?.billingAddress?.city ?? undefined,
        postalCode: currentUser?.billingAddress?.postalCode ?? undefined,
      },

      municipalRegions, usStates, provinces, countries,
      memberTypes, memberStatuses, languages,
      formatPhoneNumber, resolvePhoneNumber,
      faArrowRight, faCircleNotch,
      passwordScore: 4,

      formSubmitting: false,
      serverErrors: [] as string[],

      askPasswordVisible: false,
      askPasswordSubmitting: false,

      verifyEmailVisible: false,
    }},
    computed: {
      currentUser2() { return this.currentUser; },
      states() { return this.formComponent?.states ?? {}; },
      userGroups() {
        return this.$session.info.value?.groups ?? [];
      },
      userGroupIsMember() {
        return this.userGroups.includes("members") || this.userGroups.includes("membersEmailOnly");
      },
    },
    methods: {
      formResolve({ values }: FormResolverOptions): Record<string, any> {
        const errors: Record<string, any[]> = {
          fullName: [],
          username: [],
          email: [],
          preferredLanguage: [],
          organization: [],
          phone: [],
          fax: [],
          website: [],
          addressLines: [],
          city: [],
          region: [],
          province: [],
          state: [],
          country: [],
          postalCode: [],
          password: []
        };

        for (const [handle, label] of Object.entries({
          fullName: values?.memberType == 'center' || values.memberType == 'corporate'
            ? "Name of Organization" : "Full Name",
          email: "Email",
          preferredLanguage: "Preferred Language",
          phone: "Phone",
        })) {
          if (values[handle] == null || values[handle] === "") {
            errors[handle].push({ message: `${label} is a required field` });
          }
        }

        if (values.username != values.email && (values.username == null || values.username === "")) {
          errors.username.push({ message: "Username is a required field" });
        }

        if (values.email !== "" && values.email != null && !isEmailValid(values.email)) {
          errors.email.push({ message: "Email address is not a valid email address" });
        }

        if (values.changePassword === true && (values.password == null || values.password === "")) {
          errors.password.push({ message: "New password is required" });
        }

        if (this.passwordScore <= 1) {
          errors.password.push({
            message: `New password is a very weak password`
          });
        } else if (this.passwordScore === 2) {
          errors.password.push({
            message: `New password is a weak password`
          });
        }

        if (this.userGroupIsMember) {
          for (const [handle, label] of Object.entries({
            addressLines: "Address lines", city: "City", country: "Country",
          })) {
            if (values[handle] == null || values[handle] === "") {
              errors[handle].push({ message: `${label} is a required field` });
            }
          }

          if (values.country === "ca" && (values.province == null || values.province === "")) {
            errors.province.push({ message: "Province is required" });
          } else if (values.country === "us" && (values.state == null || values.state === "")) {
            errors.state.push({ message: "State is required" });
          }

          if (
            (values.organization == null || values.organization === "")
            && (this.currentUser?.memberType === "center" || this.currentUser?.memberType === "corporate")
          ) {
            errors.region.push({ message: "Organization is required" });
          }

          if (values.country === "ca" && values.province === "on" && (values.region == null || values.region === "")) {
            errors.region.push({ message: "Municipal Region is required" });
          }

          if (values.postalCode !== "" && values.postalCode != null && !isPostalCodeValid(values.postalCode)) {
            errors.postalCode.push({ message: "Postal code is not valid" });
          } else if (values.postalCode != null) {
            values.postalCode = formatPostalCode(values.postalCode);
            this.formComponent!.setFieldValue("postalCode", values.postalCode);
          }

          // https://mathiasbynens.be/demo/url-regex
          if (values.website !== "" && values.website != null && !isUrlValid(values.website)) {
            errors.website.push({ message: "Website is not a valid URL" });
          }
        }

        return { errors };
      },

      async formSubmit(submit: FormSubmitEvent) {
        if (!submit.valid) { return; }
        this.formSubmitting = true;

        if (submit.states.email.value !== this.initialValues.email || submit.states.changePassword.value === true) {
          this.askPasswordVisible = true;
        } else {
          try {
            const result = await this.$session.saveUser({
              userId: parseInt(this.currentUser!.id!),
              username: submit.states?.username?.value ?? undefined,
              fullName: submit.states.fullName.value,
              email: submit.states.email.value,
              fields: {
                preferredLanguage: submit.states.preferredLanguage.value,
                organization: submit.states?.organization?.value ?? "",
                phone: submit.states?.phone?.value ? {
                  number: submit.states?.phone?.value ?? "",
                  region: submit.states?.country?.value?.toUpperCase() ?? "CA",
                } : "",
                fax: submit.states?.fax?.value ? {
                  number: submit.states?.fax?.value ?? "",
                  region: submit.states?.country?.value?.toUpperCase() ?? "CA",
                } : "",
                website: submit.states.website?.value ?? "",
                billingAddress: {
                  fields: {
                    addressLines: submit.states?.addressLines?.value ?? "",
                    region: submit.states?.region?.value ?? "",
                    city: submit.states?.city?.value ?? "",
                    province: submit.states?.province?.value ?? "",
                    state: submit.states?.state?.value ?? "",
                    country: submit.states?.country?.value ?? "",
                    postalCode: submit.states?.postalCode?.value ?? "",
                  }
                },
              },
            });

            if ((Object.keys(result?.errors ?? {}).length) > 0) {
              this.serverErrors = [];
              if (result.message != null) {
                this.serverErrors.push(result.message);
              }
              if (result.errors != null) {
                this.serverErrors.push(...Object.values(result.errors).flat());
              }
            } else {
              location.reload();
            }
          } finally {
            this.formSubmitting = false;
          }
        }
      },

      askPasswordResolve({ values }: FormResolverOptions): Record<string, any> {
        const errors: Record<string, any[]> = { currentPassword: [] };

        if (values.currentPassword == null || values.currentPassword === "") {
          errors.currentPassword.push({ message: "Current password is required" });
        }

        return { errors };
      },

      async askPasswordSubmit(submit: FormSubmitEvent) {
        if (!submit.valid) { return; }
        this.askPasswordSubmitting = true;
        const form = this.formComponent!;
        try {
          const options: UserOptions = {
            userId: parseInt(this.currentUser!.id!),
            currentPassword: submit.states.currentPassword.value,
            username: submit.states?.username?.value ?? undefined,
            fullName: form.states.fullName.value,
            email: form.states.email.value,
            fields: {
              preferredLanguage: form.states.preferredLanguage.value,
              organization: submit.states?.organization?.value ?? "",
              phone: form.states?.phone?.value ? {
                number: form.states?.phone?.value ?? "",
                region: form.states?.country?.value?.toUpperCase() ?? "CA",
              } : "",
              fax: form.states.fax?.value ?? "",
              billingAddress: {
                fields: {
                  addressLines: form.states?.addressLines?.value ?? "",
                  region: form.states?.region?.value ?? "",
                  city: form.states?.city?.value ?? "",
                  province: form.states?.province?.value ?? "",
                  state: form.states?.state?.value ?? "",
                  country: form.states?.country?.value ?? "",
                  postalCode: form.states?.postalCode?.value ?? "",
                }
              },
            },
          };

          if (form.states.changePassword.value === true) {
            options.newPassword = form.states.password.value;
          }

          const result = await this.$session.saveUser(options);

          if ((Object.keys(result?.errors ?? {}).length) > 0) {
            this.serverErrors = [];
            if (result.message != null) {
              this.serverErrors.push(result.message);
            }
            if (result.errors != null) {
              this.serverErrors.push(...Object.values(result.errors).flat());
            }
          } else if (form.states.email.value !== this.initialValues.email) {
            this.verifyEmailVisible = true;
          } else {
            location.reload();
          }
        } finally {
          this.askPasswordSubmitting = false;
        }
      },

      reload() { location.reload(); },
    },
  };
</script>

<template>
  <h2>Edit Account Information</h2>
  <p>Edit your account information. The information provided here is for ADCO's billing and isn't visible to the public.</p>
  <Form v-slot="form" class="form" ref="formComponent" :initial-values="initialValues"
    validate-on-value-update validate-on-submit validate-on-blur
    :resolver="formResolve" @submit="formSubmit">
    <div v-if="userGroupIsMember" class="readonly">
      <FloatLabel variant="over">
        <InputText :id="`${vid}-membernum`" disabled fluid
          :default-value="initialValues.memberNumber"></InputText>
        <label :for="`${vid}-membernum`">
          Member Number<span class="required-star" aria-label="Required field, cannot be changed" title="Required field, cannot be changed"></span>
        </label>
      </FloatLabel>
    </div>

    <div v-if="userGroupIsMember" class="readonly">
      <FloatLabel variant="over">
        <InputText :id="`${vid}-membertype`" disabled fluid
          :default-value="initialValues.memberType != null && initialValues.memberType in memberTypes ? memberTypes[initialValues.memberType as keyof typeof memberTypes] : undefined"></InputText>
        <label :for="`${vid}-membertype`">
          Member Type<span class="required-star" aria-label="Required field, cannot be changed" title="Required field, cannot be changed"></span>
        </label>
      </FloatLabel>
    </div>

    <div class="readonly"
      v-if="userGroupIsMember && initialValues.memberType != null && (initialValues.memberType == 'center' || initialValues.memberType == 'corporate')">
      <FloatLabel variant="over">
        <InputText :id="`${vid}-memberstatus`" disabled fluid
          :default-value="initialValues.memberStatus != null && initialValues.memberStatus in memberStatuses ? memberStatuses[initialValues.memberStatus as keyof typeof memberStatuses] : undefined"></InputText>
        <label :for="`${vid}-memberstatus`">
          Business Structure<span class="required-star" aria-label="Required field, cannot be changed" title="Required field, cannot be changed"></span>
        </label>
      </FloatLabel>
    </div>

    <div class="readonly"
      v-if="userGroupIsMember && initialValues.memberType != null && (initialValues.memberType ==
      'center')">
      <FloatLabel variant="over">
        <InputText :id="`${vid}-numsites`" disabled fluid
          :default-value="initialValues.numSites"></InputText>
        <label :for="`${vid}-numsites`">
          Number of Childcare Sites<span class="required-star" aria-label="Required field, cannot be changed" title="Required field, cannot be changed"></span>
        </label>
      </FloatLabel>

      <Message class="full" severity="secondary" size="small" variant="simple">
        We consider a site as a physical childcare location.
      </Message>
    </div>

    <Message v-if="userGroupIsMember" class="full" severity="secondary" size="small" variant="simple">
      The above fields may influence your membership fees. Please <a href="../../members/contact" target="_blank">contact us</a> to update them.
    </Message>

    <div class="break">
      <FloatLabel variant="on">
        <InputText :id="`${vid}-fullName`" name="fullName" fluid
          required :disabled="formSubmitting" />
        <label :for="`${vid}-fullName`">
          Full Name<span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>

      <Message v-if="(form.fullName?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.fullName.error.message }}
      </Message>
    </div>

    <div v-if="userGroupIsMember && initialValues.memberType != null && (initialValues.memberType == 'center' || initialValues.memberType == 'corporate')">
      <FloatLabel variant="on">
        <InputText :id="`${vid}-organization`" name="organization" fluid
          required :disabled="formSubmitting" />
        <label :for="`${vid}-organization`">
          Organization Name<span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>

      <Message v-if="(form.organization?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.organization.error.message }}
      </Message>
    </div>

    <div>
      <FloatLabel variant="on">
        <Select :id="`${vid}-preferredLanguage`" name="preferredLanguage" fluid required :disabled="formSubmitting"
          :options="Object.entries(languages)"
          :option-value="d => d[0]" :option-label="d => d[1].name"/>
        <label :for="`${vid}-preferredLanguage`">
          Preferred Language<span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>

      <Message v-if="(form.preferredLanguage?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.preferredLanguage.error.message }}
      </Message>
    </div>

    <h3 class="full">Login Details</h3>
    <div>
      <FloatLabel variant="on">
        <InputText :id="`${vid}-username`" name="username" fluid required
          :disabled="formSubmitting"/>
        <label :for="`${vid}-username`">
          Username<span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>

      <Message v-if="(form.username?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.username.error.message }}
      </Message>
    </div>

    <div>
      <FloatLabel variant="on">
        <InputText :id="`${vid}-email`" name="email" fluid required
          :disabled="formSubmitting"/>
        <label :for="`${vid}-email`">
          Email Address<span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>

      <Message v-if="formComponent?.states?.email?.value !== initialValues.email" severity="secondary" size="small" variant="simple">
        An email will be sent to the new email address to verify it.
      </Message>

      <Message v-if="(form.email?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.email.error.message }}
      </Message>
    </div>

    <div class="break inline-field">
      <Checkbox :input-id="`${vid}-changepassword`" name="changePassword" binary
        :disabled="formSubmitting"/>
      <label class="inline-label" :for="`${vid}-changepassword`">
        <span>Change password</span>
      </label>
    </div>

    <div class="full" v-if="states?.changePassword?.value === true">
      <FloatLabel variant="on">
        <Password :id="`${vid}-password`" name="password" fluid required
          toggleMask feedback
          :disabled="formSubmitting">
          <template #content>
            <PasswordStrengthSuspend lang="en" :password="form!.password!.value"
              @update:score="passwordScore = $event"/>
          </template>
        </Password>
        <label :for="`${vid}-username`">
          New Password<span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>

      <Message v-if="(form.password?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.password.error.message }}
      </Message>
    </div>

    <h3 class="full" v-if="userGroupIsMember">Contact Details</h3>
    <div v-if="userGroupIsMember">
      <PrimeFormField name="phone"
        :resolver="o => {
          formComponent!.setFieldValue('phone', formatPhoneNumber(o.value));
          const result = resolvePhoneNumber(o, 'Phone number');
          if (o.value == null || o.value == '') {
            result.errors.push({ message: 'Phone number is required'});
          }
          return result;
        }">
        <FloatLabel variant="on">
          <InputText :id="`${vid}-phone`" name="phone" inputmode="tel" fluid required :disabled="formSubmitting" />
          <label :for="`${vid}-phone`">
            Phone Number<span class="required-star" aria-label="Required field" title="Required field"></span>
          </label>
        </FloatLabel>
      </PrimeFormField>

      <Message v-if="(form.phone?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.phone.error.message }}
      </Message>
    </div>

    <div v-if="userGroupIsMember">
      <PrimeFormField name="fax"
        :resolver="o => {
          formComponent!.setFieldValue('fax', formatPhoneNumber(o.value));
          return resolvePhoneNumber(o, 'Fax number');
        }">
        <FloatLabel variant="on">
          <InputText :id="`${vid}-fax`" name="fax" inputmode="tel" fluid :disabled="formSubmitting" />
          <label :for="`${vid}-fax`">Fax Number</label>
        </FloatLabel>
      </PrimeFormField>

      <Message v-if="(form.fax?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.fax.error.message }}
      </Message>
    </div>

    <div v-if="userGroupIsMember">
      <FloatLabel variant="on">
        <InputText :id="`${vid}-website`" name="website" fluid required
          :disabled="formSubmitting"/>
        <label :for="`${vid}-website`">Website</label>
      </FloatLabel>

      <Message v-if="(form.website?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.website.error.message }}
      </Message>
    </div>

    <h3 class="full" v-if="userGroupIsMember">Billing Address</h3>
    <div class="double" v-if="userGroupIsMember">
      <FloatLabel variant="on">
        <Textarea :id="`${vid}-addressLines`" name="addressLines" fluid required :disabled="formSubmitting"
          rows="3" style="resize: none;" placeholder="Unit 1
123 Main Street"/>
        <label :for="`${vid}-addressLines`">
          Address Lines<span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>

      <Message class="full" severity="secondary" size="small" variant="simple">
        All address lines as written on mail, excluding lines for city, country, or postal code.
      </Message>

      <Message v-if="(form.addressLines?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.addressLines.error.message }}
      </Message>
    </div>

    <div v-if="userGroupIsMember">
      <FloatLabel variant="on">
        <InputText :id="`${vid}-city`" name="city" fluid required :disabled="formSubmitting"/>
        <label :for="`${vid}-city`">
          City<span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>

      <Message v-if="(form.city?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.city.error.message }}
      </Message>
    </div>

    <div v-if="userGroupIsMember && states?.country?.value == 'ca' && states?.province?.value == 'on'">
      <FloatLabel variant="on">
        <Select :id="`${vid}-region`" name="region" fluid required :disabled="formSubmitting"
          :options="Object.entries(municipalRegions)"
          :option-value="d => d[0]" :option-label="d => d[1].name"
          filter filter-placeholder="Search"
          :pt="{ listContainer: { style: { 'max-height': '25rem' }} }">
          <template #option="slot">
            <div style="display: flex; flex-direction: column;">
              <span>{{ slot.option[1].name }}</span>
              <small>{{ slot.option[1].long }}</small>
            </div>
          </template>
        </Select>
        <label :for="`${vid}-region`">
          Municipal Region<span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>

      <Message severity="secondary" size="small" variant="simple">
        Uncertain about your region? <a href="https://www.amo.on.ca/about-us/municipal-101/ontario-municipalities" target="_blank">Identify your municipality by city.</a>
      </Message>

      <Message v-if="(form.region?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.region.error.message }}
      </Message>
    </div>

    <div v-if="userGroupIsMember && states?.country?.value == 'ca'" class="break">
      <FloatLabel variant="on">
        <Select :id="`${vid}-province`" name="province" fluid required :disabled="formSubmitting"
          :options="Object.entries(provinces)"
          :option-value="d => d[0]" :option-label="d => d[1]"/>
        <label :for="`${vid}-province`">
          Province<span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>
    </div>

    <div v-if="userGroupIsMember && states?.country?.value == 'us'" class="break">
      <FloatLabel variant="on">
        <Select :id="`${vid}-state`" name="state" fluid required :disabled="formSubmitting"
          :options="Object.entries(usStates)"
          :option-value="d => d[0]" :option-label="d => d[1]"
          filter filter-placeholder="Search"/>
        <label :for="`${vid}-state`">
          State<span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>
    </div>

    <div v-if="userGroupIsMember">
      <FloatLabel variant="on">
        <Select :id="`${vid}-country`" name="country" fluid required :disabled="formSubmitting"
          :options="Object.entries(countries)"
          :option-value="d => d[0]" :option-label="d => d[1]"
          filter filter-placeholder="Search"/>
        <label :for="`${vid}-country`">
          Country<span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>
    </div>

    <div v-if="userGroupIsMember">
      <FloatLabel variant="on">
        <InputText :id="`${vid}-postalCode`" name="postalCode" fluid :disabled="formSubmitting" />
        <label :for="`${vid}-postalCode`">Postal Code</label>
      </FloatLabel>

      <Message v-if="(form.postalCode?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.postalCode.error.message }}
      </Message>
    </div>

    <Message class="full" v-for="error in serverErrors" severity="error" size="small" variant="simple">
      {{ error }}
    </Message>

    <Button class="full" type="submit" label="Save"
      formnovalidate :disabled="!form.valid || formSubmitting"
      icon-pos="right">
      <template #icon="slot">
        <Icon :class="['fa', slot.class]" :icon="formSubmitting ? faCircleNotch : faArrowRight" :spin="formSubmitting"/>
      </template>
    </Button>
  </Form>

  <Dialog v-model:visible="askPasswordVisible"
    modal block-scroll :draggable="false"
    :close-on-escape="!askPasswordSubmitting"
    :dismissable-mask="!askPasswordSubmitting"
    :closable="!askPasswordSubmitting"
    header="Verify Your Identity"
    @close="formSubmitting = false;"
    @hide="formSubmitting = false;">
    <Form style="max-width: 40ch;" class="form" v-slot="askPasswordForm"
      validate-on-value-update validate-on-submit validate-on-blur
      :resolver="askPasswordResolve" @submit="askPasswordSubmit">
      <p class="full">Please enter your current password to change your email or password.</p>
      <p class="full">Forgot your current password? <a href="" target="_blank">Reset it here</a></p>
      <div class="full">
        <FloatLabel variant="on">
          <Password :input-id="`${vid}-currentpassword`" name="currentPassword" required fluid
            :feedback="false" toggleMask :disabled="askPasswordSubmitting"/>
          <label :for="`${vid}-currentpassword`">
            Current Password<span class="required-star" aria-label="Required field" title="Required field"></span>
          </label>
        </FloatLabel>

        <Message v-if="(askPasswordForm.currentPassword?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
          {{ askPasswordForm.currentPassword.error.message }}
        </Message>
      </div>

      <Message class="full" v-for="error in serverErrors" severity="error" size="small" variant="simple">
        {{ error }}
      </Message>

      <Button class="full" type="submit" label="Continue"
        formnovalidate :disabled="!askPasswordForm.valid || askPasswordSubmitting"
        icon-pos="right">
        <template #icon="slot">
          <Icon :class="['fa', slot.class]" :icon="askPasswordSubmitting ? faCircleNotch : faArrowRight" :spin="askPasswordSubmitting"/>
        </template>
      </Button>
    </Form>
  </Dialog>

  <Dialog v-model:visible="verifyEmailVisible"
    modal block-scroll :draggable="false"
    :close-on-escape="false" :dismissable-mask="false" :closable="false"
    header="Verify Your New Email Address">
    <div style="max-width: 60ch;" class="form">
      <p class="full">An email has been sent to {{ formComponent!.states!.email.value }} to verify the email address. Please follow the link in the email to verify it. The old email will remain on the account until the new email is verified.</p>
      <Button class="full" label="Ok" formnovalidate icon-pos="right" @click="reload">
        <template #icon="slot">
          <Icon :class="['fa', slot.class]" :icon="faArrowRight"/>
        </template>
      </Button>
    </div>
  </Dialog>
</template>

<style lang="less" scoped>
  h3 { text-align: center; }

  .form {
    display: grid;
    grid: auto-flow / 1fr 1fr;
    margin-block: var(--spacing-block);
    width: 100%;
    max-width: 90ch;

    gap: var(--spacing-block);
    align-items: flex-start;
    justify-items: stretch;

    & .break { grid-column-start: 1; }
    & .full { grid-column: 1 / span 2; }
    & .double { grid-row-end: span 2; }
  }

  .inline-field {
    display: inline-flex;
    align-items: center;
    vertical-align: bottom;
    .inline-label {
      margin-inline: var(--spacing-inline);
    }
  }

  .required-star::before {
    content: "*";
    color: var(--p-floatlabel-invalid-color);
  }

  .readonly {
    // border: none;
    box-shadow: none;
    pointer-events: none;

    padding-top: var(--spacing-block);
  }
</style>
